import { plural, t } from '@lingui/macro'
import { addDays, addMonths, format, getYear, startOfDay } from 'date-fns/esm'
import { nl } from 'date-fns/locale'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import { MultiSelect } from '@/@bothrs-ui/web/components/forms/select/MultiSelect'
import { FlexColumn, FlexRow, Padding } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Skeleton } from '@/@bothrs-ui/web/components/skeleton/Skeleton'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { breakpoints } from '@/config/layout'
import { mixpanel } from '@/mixpanel'
import { useDoctorFilter } from '../hooks/useDoctorFilter'
import { type ExtendedDoctorSearchPhase, useExtendedDoctorSearch } from '../hooks/useExtendedDoctorSearch'
import { useSlotAccumulator } from '../hooks/useSlotAccumulator'
import { BookingFlowCenteredContent, BookingFlowLayout } from './BookingFlowLayout'
import { DateSelectionCarousel } from './DateSelectionCarousel'
import { defaultFilter, FilterModal } from './FilterModal'
import { SlotList } from './SlotList'

import type { MutableRefObject } from 'react'
import type { FlowFilters, FlowState, FlowStep, SlotWithLocationAndDoctor } from '../types/booking.type'

// Extended-search windowing. The initial search covers the next 6 months from
// the chosen start date; each "Zoek verder" adds another 6 months. We never
// search past 18 months from today, so a start date capped at 1 year ahead
// (see FilterModal) always lands within reach of the ceiling.
const INITIAL_SEARCH_MONTHS = 6
const SEARCH_STEP_MONTHS = 6
const MAX_MONTHS_FROM_TODAY = 18

function uncheckAllDoctors(doctors: FlowFilters['doctors']): FlowFilters['doctors'] {
  return Object.entries(doctors ?? {}).reduce(
    (accumulator, [key, doctor]) => ({
      ...accumulator,
      [key]: { ...doctor, checked: false },
    }),
    {} as FlowFilters['doctors'],
  )
}

type SlotSelectionEmptyStateProps = {
  phase: ExtendedDoctorSearchPhase
  hasCheckedDoctor: boolean
  canSwitchDoctor: boolean
  canExtend: boolean
  searchedEndLabel: string
  searchEndWithoutYearLabel: string
  nextEndLabel: string
  doctorsValue: ReadonlyArray<{ label: string }>
  service: string | undefined
  onExtendSearch: () => void
  onShowAllDoctors: () => void
  // Owned by the parent so the dedup survives this component remounting when
  // the carousel toggles on and off.
  trackedMessageKey: MutableRefObject<string | null>
}

function SlotSelectionEmptyState({
  phase,
  hasCheckedDoctor,
  canSwitchDoctor,
  canExtend,
  searchedEndLabel,
  searchEndWithoutYearLabel,
  nextEndLabel,
  doctorsValue,
  service,
  onExtendSearch,
  onShowAllDoctors,
  trackedMessageKey,
}: SlotSelectionEmptyStateProps) {
  const theme = useTheme()

  // Track each distinct no-slots message once, as the denominator for the
  // CTR on the "Zoek verder" / "Toon de beschikbaarheden van alle artsen van de dienst {service}" links.
  const isNoSlotsMessage = (phase.kind === 'idle' || phase.kind === 'search-settled') && hasCheckedDoctor
  const scope = canExtend && phase.extendCount === 0 ? 'six_months' : 'extended'
  useEffect(() => {
    if (!isNoSlotsMessage) return
    const key = `${scope}-${phase.extendCount}`
    if (trackedMessageKey.current === key) return
    trackedMessageKey.current = key
    mixpanel.track('appointment_slotselect_no_slots_message_shown', {
      scope,
      doctor_count: doctorsValue.length,
    })
  }, [isNoSlotsMessage, scope, phase.extendCount, doctorsValue.length])

  if (phase.kind === 'searching') {
    return (
      <StEmptyStateText>
        {t({
          id: 'appointments.book.slots.searching-extended',
          message: 'Bezig met zoeken tot {endDate}…',
          values: { endDate: phase.endLabel },
        })}
      </StEmptyStateText>
    )
  }

  // Resting empty state with a doctor selected: offer another 6-month
  // extension while the 18-month ceiling has not been reached, otherwise show
  // the terminal message and fall back to showing all doctors.
  if ((phase.kind === 'idle' || phase.kind === 'search-settled') && hasCheckedDoctor) {
    if (canExtend) {
      return (
        <FlexColumn gap={theme.UI.SpacingPx.Space2} alignItems='center'>
          <StEmptyStateText>
            {phase.extendCount === 0
              ? t({
                  id: 'appointments.book.slots.no-slots-6-months',
                  message: 'Geen beschikbare momenten gevonden in de komende 6 maanden.',
                })
              : t({
                  id: 'appointments.book.slots.no-slots-until',
                  message: 'Geen beschikbare momenten gevonden tot {endDate}.',
                  values: { endDate: searchedEndLabel },
                })}
          </StEmptyStateText>
          <StTextLinkButton onClick={onExtendSearch}>
            {t({
              id: 'appointments.book.slots.search-further',
              message: 'Zoek verder vanaf {startDate} tot {endDate}',
              values: {
                startDate: searchEndWithoutYearLabel,
                endDate: nextEndLabel,
              },
            })}
          </StTextLinkButton>
        </FlexColumn>
      )
    }

    return (
      <FlexColumn gap={theme.UI.SpacingPx.Space2} alignItems='center'>
        <StEmptyStateText>
          {doctorsValue.length === 1
            ? t({
                id: 'appointments.book.slots.no-slots-doctor',
                message: '{name} heeft geen beschikbare momenten tot {endDate}.',
                values: {
                  name: doctorsValue[0].label,
                  endDate: searchedEndLabel,
                },
              })
            : t({
                id: 'appointments.book.slots.no-slots-doctors',
                message: 'De geselecteerde artsen hebben geen beschikbare momenten tot {endDate}.',
                values: { endDate: searchedEndLabel },
              })}
        </StEmptyStateText>
        {canSwitchDoctor ? (
          <StTextLinkButton onClick={onShowAllDoctors}>
            {t({
              id: 'appointments.book.slots.show-all-doctors',
              message: 'Toon de beschikbaarheden van alle artsen van de dienst {service}',
              values: { service },
            })}
          </StTextLinkButton>
        ) : null}
      </FlexColumn>
    )
  }

  return (
    <StEmptyStateText>
      {t({
        id: 'appointments.book.slots.no-slots-with-filters',
        message: 'Met de huidige ingestelde filters zijn er geen slots beschikbaar.',
      })}
    </StEmptyStateText>
  )
}

type SlotSelectionContentProps = {
  step: FlowStep
  flowState: FlowState
  onSlotSelect: (
    action: 'select-slot',
    data?: {
      slot: SlotWithLocationAndDoctor
    },
  ) => void
  onLoadMoreSlots: (
    action: 'load-more-slots',
    data?: {
      fromDate: string
      tillDate?: string
      numberOfDays?: number
    },
  ) => Promise<void> | void
  onFilterUpdate: (
    action: 'update-filters',
    data?: {
      filters: FlowFilters
    },
  ) => void
  isLoading: boolean
  isLoadingMore: boolean
}

export const SlotSelectionContent = ({
  step,
  flowState,
  onSlotSelect,
  onLoadMoreSlots,
  onFilterUpdate,
  isLoading,
  isLoadingMore,
}: SlotSelectionContentProps) => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()

  // State
  // ------------------------------------------------------------------------- /
  const stepSlots = useMemo(() => (step.data?.slots || []) as SlotWithLocationAndDoctor[], [step.data?.slots])

  const { accumulatedSlots, selectedDate, setSelectedDate, isStale, markFiltersChanged } = useSlotAccumulator(
    stepSlots,
    isLoading,
  )

  const [showFilterModal, setShowFilterModal] = useState(false)

  // Held here rather than in SlotSelectionEmptyState so the no-slots dedup
  // survives that component remounting when the carousel toggles.
  const trackedNoSlotsMessageKey = useRef<string | null>(null)

  const { phase, startSearch, settleSearch, reset } = useExtendedDoctorSearch()

  // Template variables
  // ------------------------------------------------------------------------- /
  const hasMore = step.data?.hasMore ?? false

  // Extended search date window. `searchedEnd` is how far we've already
  // searched (start date + initial 6 months + 6 per completed extension);
  // `nextEnd` is the window the next "Zoek verder" would cover. Both are
  // clamped to 18 months from today, so the query window starts exactly where
  // the previous one ended (no overlap) and never runs past the ceiling.
  const { canExtend, tillYear, tillDate, extendedFromDate, searchedEndLabel, searchEndWithoutYearLabel, nextEndLabel } =
    useMemo(() => {
      const today = new Date()
      const ceiling = startOfDay(addMonths(today, MAX_MONTHS_FROM_TODAY))
      const fromDate = new Date(flowState.filters?.fromDate || today)
      const clampToCeiling = (date: Date) => (date > ceiling ? ceiling : date)

      const searchedEnd = clampToCeiling(
        addMonths(fromDate, INITIAL_SEARCH_MONTHS + SEARCH_STEP_MONTHS * phase.extendCount),
      )
      const nextEnd = clampToCeiling(addMonths(searchedEnd, SEARCH_STEP_MONTHS))

      return {
        canExtend: searchedEnd < ceiling,
        tillYear: getYear(nextEnd),
        tillDate: format(nextEnd, 'yyyy-MM-dd'),
        extendedFromDate: format(searchedEnd, 'yyyy-MM-dd'),
        searchedEndLabel: format(searchedEnd, 'd MMMM yyyy', { locale: nl }),
        searchEndWithoutYearLabel: format(searchedEnd, 'd MMMM', { locale: nl }),
        nextEndLabel: format(nextEnd, 'd MMMM yyyy', { locale: nl }),
      }
    }, [flowState.filters?.fromDate, phase.extendCount])

  // Group slots by date
  const groupedSlots = useMemo(() => {
    return accumulatedSlots.reduce(
      (accumulator, slot) => {
        if (!slot.start) return accumulator // Skip slots without start time
        const dateKey = slot.start.slice(0, 10) // e.g., "2030-03-14"
        if (!accumulator[dateKey]) accumulator[dateKey] = []
        accumulator[dateKey].push(slot)
        return accumulator
      },
      {} as Record<string, SlotWithLocationAndDoctor[]>,
    )
  }, [accumulatedSlots, selectedDate])

  // Handlers
  // ------------------------------------------------------------------------- /

  /**
   * Handle selectedDate changes (user input only)
   */
  const handleSelectedDateChange = (newDate: string) => {
    setSelectedDate(newDate)

    // Only load more slots if the selected date doesn't have slots yet
    if (!groupedSlots[newDate] || groupedSlots[newDate].length === 0) {
      onLoadMoreSlots('load-more-slots', { fromDate: newDate })
    } else {
      // Fetch slots for that date
      onLoadMoreSlots('load-more-slots', {
        fromDate: newDate,
        tillDate: format(addDays(new Date(newDate), 1), 'yyyy-MM-dd'),
        numberOfDays: 1,
      })
    }
  }

  /**
   * Open the filter modal
   */
  const openFilterModal = () => {
    setShowFilterModal(true)
  }

  const handleUpdateFilters = useCallback(
    (action: 'update-filters', data?: { filters: FlowFilters }) => {
      reset()
      markFiltersChanged()
      onFilterUpdate(action, {
        filters: data?.filters ?? { ...defaultFilter },
      })
    },
    [reset, markFiltersChanged, onFilterUpdate],
  )

  const handleExtendSearch = useCallback(async () => {
    startSearch(nextEndLabel)
    try {
      await onLoadMoreSlots('load-more-slots', {
        fromDate: extendedFromDate,
        tillDate,
      })
    } finally {
      settleSearch()
    }
    mixpanel.track('appointment_slotselect_search_extended', { year: tillYear })
  }, [startSearch, settleSearch, onLoadMoreSlots, extendedFromDate, tillDate, nextEndLabel, tillYear])

  // Doctor filter
  // ------------------------------------------------------------------------- /
  const filters = flowState.filters ?? defaultFilter

  const {
    doctorOptions,
    doctorsValue,
    hasCheckedDoctor,
    handleDoctorChange: onDoctorChange,
    clearSelection: clearDoctorSelection,
  } = useDoctorFilter({ filters, onFilterUpdate: handleUpdateFilters })

  const handleDoctorChange = useCallback(
    (selected: Parameters<typeof onDoctorChange>[0]) => {
      reset()
      onDoctorChange(selected)
    },
    [onDoctorChange, reset],
  )

  // Booking via a recent caregiver narrows the selector to that one agenda, as
  // does a discipline with a single doctor. There is then nothing to switch to
  // and nothing wider to show, and dropping that doctor would only strand the
  // patient on an empty screen — the empty state needs a checked doctor before
  // it offers to extend the search.
  const canSwitchDoctor = doctorOptions.length > 1

  const onClearFilters = () => {
    if (canSwitchDoctor) {
      clearDoctorSelection()
    }
    handleUpdateFilters('update-filters', {
      filters: {
        ...defaultFilter,
        doctors: canSwitchDoctor ? uncheckAllDoctors(flowState.filters?.doctors) : flowState.filters?.doctors,
      },
    })
  }

  const handleShowAllDoctors = useCallback(() => {
    clearDoctorSelection()
    handleUpdateFilters('update-filters', {
      filters: {
        ...flowState.filters,
        fromDate: format(new Date(), 'yyyy-MM-dd'),
        doctors: uncheckAllDoctors(flowState.filters?.doctors),
      },
    })
    mixpanel.track('appointment_slotselect_show_all_doctors')
  }, [clearDoctorSelection, flowState.filters, handleUpdateFilters])

  // Compute active filter count excluding doctors (doctors are now a separate control)
  const nonDoctorActiveFilterCount = Math.max(0, (flowState.numberOfActiveFilters ?? 0) - (hasCheckedDoctor ? 1 : 0))

  /**
   * Load more dates
   */
  const handleLoadMoreDates = useCallback(() => {
    if (!hasMore || isLoading || isLoadingMore) {
      return
    }
    // The frontier is the latest date we hold, not the last slot appended: a
    // single-date refresh appends its slots at the tail even when that date is
    // earlier than ones already loaded, which would drag fromDate backwards.
    const latestStart = accumulatedSlots.reduce<string | null>(
      (latest, slot) => (slot.start && (!latest || slot.start > latest) ? slot.start : latest),
      null,
    )
    const fromDate = latestStart
      ? format(addDays(new Date(latestStart.slice(0, 10)), 1), 'yyyy-MM-dd')
      : format(new Date(), 'yyyy-MM-dd')

    // Call your navigation endpoint with action 'load-more-slots'
    // This depends on how you handle API calls, but for example:
    onLoadMoreSlots('load-more-slots', { fromDate })
  }, [hasMore, isLoading, isLoadingMore, accumulatedSlots, onLoadMoreSlots])

  // Render
  // ------------------------------------------------------------------------- /
  const showCarousel = Object.keys(groupedSlots).length > 0 || (phase.kind === 'idle' && (isLoading || isStale))

  return (
    <BookingFlowLayout>
      <BookingFlowCenteredContent>
        <FlexColumn gap={theme.UI.SpacingPx.Space4}>
          <Span type='BodyLargeRegular'>
            {t({
              id: 'appointments.book.slot-selection.title',
              comment: 'Slot selection title',
              message: 'Kies een afspraakmoment. Duid een specifieke arts aan indien gewenst.',
            })}
          </Span>

          <StStickyHeader>
            <FlexColumn gap={theme.UI.SpacingPx.Space4}>
              {canSwitchDoctor ? (
                <MultiSelect
                  name='doctors'
                  placeholder={t({
                    id: 'appointments.book.slots.doctor-placeholder',
                    message: 'Selecteer één of meerdere artsen',
                  })}
                  value={doctorsValue}
                  options={doctorOptions}
                  onChange={handleDoctorChange}
                  onMenuOpen={() => {
                    mixpanel.track('appointment_slotselect_doctor_open')
                  }}
                  // selectionSummary={(count) =>
                  //   t({
                  //     id: 'appointments.book.slots.doctor-summary',
                  //     message: plural(count, {
                  //       one: `${count} arts geselecteerd`,
                  //       other: `${count} artsen geselecteerd`,
                  //     }),
                  //   })
                  // }
                />
              ) : null}

              <FlexRow gap={theme.UI.SpacingPx.Space2}>
                {isLoading ? (
                  <Skeleton
                    style={{
                      display: 'block',
                      height: theme.UI.SpacingPx.Space12,
                    }}
                  />
                ) : (
                  <>
                    <Button
                      variant='Primary'
                      onClick={openFilterModal}
                      suffixIcon={['far', 'bars-filter']}
                      style={{ fontWeight: 'bold' }}
                    >
                      {nonDoctorActiveFilterCount > 0
                        ? t({
                            id: 'appointments.book.slots.active-filters',
                            message: plural(nonDoctorActiveFilterCount, {
                              one: `${nonDoctorActiveFilterCount} actieve filter`,
                              other: `${nonDoctorActiveFilterCount} actieve filters`,
                            }),
                          })
                        : t({
                            id: 'appointments.book.slots.filter',
                            message: 'Filter op tijdstip of locatie',
                          })}
                    </Button>
                    {nonDoctorActiveFilterCount > 0 || (hasCheckedDoctor && canSwitchDoctor) ? (
                      <Button variant='Primary' onClick={onClearFilters} suffixIcon={['far', 'close']}>
                        {t({
                          id: 'appointments.book.slots.clear-filters',
                          message: 'Filters wissen',
                        })}
                      </Button>
                    ) : null}
                  </>
                )}
              </FlexRow>

              {showCarousel && (
                <DateSelectionCarousel
                  groupedSlots={groupedSlots}
                  selectedDate={selectedDate ?? ''}
                  hasMore={hasMore}
                  isLoading={isLoading || isStale}
                  isLoadingMore={isLoadingMore}
                  setSelectedDate={handleSelectedDateChange}
                  onLoadMoreDates={handleLoadMoreDates}
                />
              )}
            </FlexColumn>
          </StStickyHeader>

          {!showCarousel && (
            <SlotSelectionEmptyState
              phase={phase}
              hasCheckedDoctor={hasCheckedDoctor}
              canSwitchDoctor={canSwitchDoctor}
              canExtend={canExtend}
              searchedEndLabel={searchedEndLabel}
              searchEndWithoutYearLabel={searchEndWithoutYearLabel}
              nextEndLabel={nextEndLabel}
              doctorsValue={doctorsValue}
              service={flowState.specialisation?.title.nl}
              onExtendSearch={handleExtendSearch}
              onShowAllDoctors={handleShowAllDoctors}
              trackedMessageKey={trackedNoSlotsMessageKey}
            />
          )}

          <Padding pb={theme.UI.Spacing.Space4}>
            <SlotList
              key={selectedDate}
              slots={groupedSlots?.[selectedDate ?? ''] || []}
              onSlotSelect={onSlotSelect}
              isLoading={isLoading}
              isStale={isStale}
            />
          </Padding>
        </FlexColumn>
      </BookingFlowCenteredContent>

      <FilterModal
        isOpen={showFilterModal}
        flowState={flowState}
        filters={flowState.filters ?? defaultFilter}
        onClose={() => {
          setShowFilterModal(false)
        }}
        onUpdate={handleUpdateFilters}
      />
    </BookingFlowLayout>
  )
}

const StEmptyStateText = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.Colors.Neutrals.Dark};
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4};
  margin: 0;
`

const StTextLinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${({ theme }) => theme.Colors.Theme.Primary};
  cursor: pointer;
  text-decoration: underline;
  text-align: center;
  font: inherit;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.Colors.Theme.Primary};
    outline-offset: 2px;
    border-radius: 2px;
  }
`

const StStickyHeader = styled.div`
  position: sticky;
  top: -${({ theme }) => theme.UI.SpacingPx.Space4};
  padding-top: ${({ theme }) => theme.UI.SpacingPx.Space4};

  z-index: 1;
  background-color: ${({ theme }) => theme.Colors.Background.White};

  @media ${breakpoints.m} {
    padding-top: 0;
  }
`
